import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Box,
  useDisclosure,
  Tooltip,
  MenuDivider,
} from '@chakra-ui/react'
import { AddIcon } from '@chakra-ui/icons'
import React, { useContext, useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MLContext, UserContext } from '../../../providers'
import { ACLPermissionEnum } from '../../../constants'
import { isRestrictedUser } from '../../../../utils/roles'
import CreateFolder from './CreateFolder'
import FileUpload from './FileUpload'
import FolderUpload from './FolderUpload'
import NewButtonCTA from '../GenerateImage/NewButtonCTA'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'

const NewButton = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const [
    {
      folderPermission,
      loadingFolderPermission,
      currentMediaCollection,
      contextMenu,
      openAdvancedSearch,
      openSemanticImageSearch,
    },
  ] = useContext(MLContext)

  const {
    isOpen: isOpenNewFolderModal,
    onOpen: onOpenNewFolderModal,
    onClose: onCloseNewFolderModal,
  } = useDisclosure()
  const [isFileUploadModalOpen, openFileUploadModal] = useState(false)
  const [isFolderUploadModalOpen, openFolderUploadModal] = useState(false)
  const [isHidden, setIsHidden] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [isSearchBarFocused, setIsSearchBarFocused] = useState<boolean>(false)

  const [onFilesDrop, setOnFilesDropFunction] = useState<Function | undefined>()
  const [onFileOrDirectoryDrop, setOnFileOrDirectoryDropFunction] = useState<
    Function | undefined
  >()
  const { url } = useRouteMatch()
  useEffect(() => {
    const mediaCollectionPageRegex = new RegExp(
      `^/media-collections/${currentMediaCollection}/?$`,
      'u',
    )
    if (mediaCollectionPageRegex.test(url)) {
      setIsHidden(true)
    } else if (
      user?.roles &&
      isRestrictedUser(user.roles) &&
      /media-library\/?$/u.test(url)
    ) {
      setIsHidden(true)
    } else {
      setIsHidden(false)
    }
    setIsDisabled(
      user?.roles && isRestrictedUser(user.roles)
        ? !(
            folderPermission === ACLPermissionEnum.CONTRIBUTE ||
            folderPermission === ACLPermissionEnum.MANAGE
          )
        : false,
    )
  }, [user, url, currentMediaCollection, folderPermission])

  useEffect(() => {
    const handleSearchBar = (event: CustomEvent): void => {
      setIsSearchBarFocused(event.detail as boolean)
    }
    window.addEventListener(
      'ik-search-bar-focused',
      handleSearchBar as EventListener,
    )

    return () => {
      window.removeEventListener(
        'ik-search-bar-focused',
        handleSearchBar as EventListener,
      )
    }
  }, [])

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent): void => {
      const clipboardItems = event.clipboardData?.items
      if (clipboardItems) {
        // If there is any text data on the clipboard, don't execute the paste handler
        // even if there is a file along with it. Microsoft Excel copies a cell
        // representation as 4 data inputs: text/plain, text/html, text/rtf and image/png.
        const hasTextData = Array.from(clipboardItems).some(item =>
          item.type.startsWith('text/'),
        )
        if (hasTextData) {
          return
        }

        for (const item of clipboardItems) {
          if (item.kind === 'file') {
            const file = item.getAsFile()
            if (file) {
              // Emit a CustomEvent with the file
              const customEvent = new CustomEvent('ik-file-pasted', {
                detail: { file },
              })

              window.dispatchEvent(customEvent)
            }
          }
        }
      }
    }

    // eslint-disable-next-line no-empty-function
    let handlerFn: (() => void) | ((event: ClipboardEvent) => void) = () => {}
    if (
      !isHidden &&
      !isDisabled &&
      !isFileUploadModalOpen &&
      !isFolderUploadModalOpen &&
      !isOpenNewFolderModal &&
      !contextMenu.visible &&
      !openAdvancedSearch &&
      !openSemanticImageSearch &&
      !isSearchBarFocused
    ) {
      handlerFn = handlePaste
    }
    document.addEventListener('paste', handlerFn)

    return () => {
      document.removeEventListener('paste', handlerFn)
    }
  }, [
    isHidden,
    isDisabled,
    isFileUploadModalOpen,
    isFolderUploadModalOpen,
    isOpenNewFolderModal,
    contextMenu.visible,
    openAdvancedSearch,
    openSemanticImageSearch,
    isSearchBarFocused,
  ])

  useEffect(() => {
    const handleFileUploadModalOpen = (): void => {
      openFileUploadModal(true)
    }

    const handleFolderUploadModalOpen = (): void => {
      openFolderUploadModal(true)
    }

    const handleNewFolderModalOpen = (): void => {
      onOpenNewFolderModal()
    }

    document.addEventListener(
      'ik-file-upload-modal-open',
      handleFileUploadModalOpen,
    )
    document.addEventListener(
      'ik-folder-upload-modal-open',
      handleFolderUploadModalOpen,
    )
    document.addEventListener(
      'ik-new-folder-modal-open',
      handleNewFolderModalOpen,
    )

    return () => {
      document.removeEventListener(
        'ik-file-upload-modal-open',
        handleFileUploadModalOpen,
      )
      document.removeEventListener(
        'ik-folder-upload-modal-open',
        handleFolderUploadModalOpen,
      )
      document.removeEventListener(
        'ik-new-folder-modal-open',
        handleNewFolderModalOpen,
      )
    }
  }, [openFileUploadModal, openFolderUploadModal, onOpenNewFolderModal])

  return (
    <Box>
      <Menu>
        <Tooltip
          hasArrow
          label={
            isDisabled
              ? t(
                  'sufficient_permission_msg',
                  'You do not have sufficient permission for this operation.',
                )
              : ''
          }
          shouldWrapChildren
          mt="1"
        >
          <MenuButton
            leftIcon={<AddIcon />}
            as={Button}
            height="33px"
            colorScheme="appBlue"
            isLoading={loadingFolderPermission}
            display={isHidden ? 'none' : 'inline-flex'}
            isDisabled={isDisabled}
            className="new-button"
          >
            New
          </MenuButton>
        </Tooltip>
        <MenuList minWidth="240px" pb={!isPublicLink ? '0' : undefined}>
          <MenuItem onClick={onOpenNewFolderModal} className="create-folder">New Folder</MenuItem>
          <MenuItem onClick={() => void openFileUploadModal(true)} className="file-upload">
            File Upload
          </MenuItem>
          <MenuItem onClick={() => void openFolderUploadModal(true)} className="folder-upload">
            Folder Upload
          </MenuItem>
          {!isPublicLink && (
            <>
              <MenuDivider mb="0" />
              <NewButtonCTA />
            </>
          )}
        </MenuList>
      </Menu>

      <CreateFolder
        isOpen={isOpenNewFolderModal}
        isSecondaryLocation={false}
        onClose={onCloseNewFolderModal}
      />

      <FileUpload
        isModalOpen={isFileUploadModalOpen}
        openModal={() => {
          openFileUploadModal(true)
        }}
        closeModal={() => {
          openFileUploadModal(false)
        }}
        setOnFilesDropFunction={setOnFilesDropFunction}
        setOnFileOrDirectoryDropFunction={setOnFileOrDirectoryDropFunction}
      />

      <FolderUpload
        isModalOpen={isFolderUploadModalOpen}
        closeModal={() => {
          openFolderUploadModal(false)
        }}
        onFilesDrop={onFilesDrop}
        onFileOrDirectoryDrop={onFileOrDirectoryDrop}
      />
    </Box>
  )
}

export default NewButton
